//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  CampaignWorkflowAffiliateConfigurationEnum,
  CreatorsCampaignsWorkflowGoogleAnalyticsConfiguration,
  CreatorsCampaignsWorkflowImpactConfiguration,
  CreatorsCampaignsWorkflowResponse,
  CreatorsCampaignsWorkflowStoreConfiguration,
} from '@apis/creators-campaigns/CreatorsCampaignsApiModel';
import {
  CreatorsCampaignInfluencerCommissionType,
  InfluencersCommission,
} from '@apis/creators-campaigns/creators-proposals/CreatorsProposalsApiModel';
import {
  AffiliateConfigurationFormValues,
  InfluencersCommissionFormValues,
  ShopifyAffiliateConfigurationFormValues,
} from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignsTypes';
import {
  isDefined,
  isEmptyValue,
  isTruthy,
  isUndefined,
} from '@utils/filterPredicates';
import { isNullOrEmpty } from '@utils/stringUtils';

/**
 * @description Checks if the  affiliate configuration of google analytics and shopify and fixed is/are complete.
 */
export const isAffiliatesAndFixedPayComplete = (
  fixedPay: CreatorsCampaignsWorkflowResponse['fixedPay'],
  googleAnalyticsConfiguration: CreatorsCampaignsWorkflowResponse['googleAnalyticsConfiguration'],
  affiliateSettingsShopifyConfig: {
    storeConfigurations: CreatorsCampaignsWorkflowResponse['storeConfigurations'];
    influencersCommission: CreatorsCampaignsWorkflowResponse['influencersCommission'];
  },
) => {
  // Validate Fixed Pay Configuration
  const isFixedPayValid = fixedPay?.enabled
    ? !isUndefined(fixedPay.value) && fixedPay.value > 0
    : true;

  // Validate Google Analytics Configuration
  const isGoogleAnalyticsValid = googleAnalyticsConfiguration?.enabled
    ? !!googleAnalyticsConfiguration.destinationUrl
    : true;

  // Extract Affiliate Settings
  const isPricingRuleEnabled =
    affiliateSettingsShopifyConfig.storeConfigurations?.pricingRule?.enabled;

  const hasPricingRuleId =
    affiliateSettingsShopifyConfig.storeConfigurations?.pricingRule
      ?.pricingRuleId;

  const commissionValue =
    affiliateSettingsShopifyConfig.influencersCommission?.commissionValue;

  const commissionType =
    affiliateSettingsShopifyConfig.influencersCommission?.commissionType;

  const isCommissionValueValid =
    isDefined(commissionValue) && commissionValue > 0;

  const isCommissionTypeValid = isDefined(commissionType);

  const isPercentageCommissionValid =
    commissionType !== CreatorsCampaignInfluencerCommissionType.PERCENTAGE ||
    (isCommissionValueValid && commissionValue <= 100);

  const isAffiliatesConfigured =
    !isPricingRuleEnabled ||
    (hasPricingRuleId &&
      isCommissionValueValid &&
      isCommissionTypeValid &&
      isPercentageCommissionValid);

  return isAffiliatesConfigured && isFixedPayValid && isGoogleAnalyticsValid;
};

/**
 * @description Checks if the shopify store and influencer commission configuration is valid.
 */
export const isStoreAndCommissionConfigValid = (affiliateFormData: {
  shopify?: ShopifyAffiliateConfigurationFormValues;
  influencersCommission?: InfluencersCommissionFormValues;
}): boolean => {
  const { influencersCommission, shopify } = affiliateFormData;

  if (
    (isDefined(shopify?.pricingRuleId) &&
      isNullOrEmpty(shopify.pricingRuleId)) ||
    !isTruthy(influencersCommission?.commissionType)
  ) {
    return false;
  }

  if (
    influencersCommission?.commissionType ===
      CreatorsCampaignInfluencerCommissionType.PERCENTAGE &&
    isUndefined(influencersCommission.commissionValuePercentage)
  ) {
    return false;
  }

  if (
    influencersCommission?.commissionType ===
      CreatorsCampaignInfluencerCommissionType.FIXED &&
    isUndefined(influencersCommission.commissionValueFixed)
  ) {
    return false;
  }

  return true;
};

/**
 * @description Checks if the Google Analytics configuration is valid.
 */
export const isGoogleAnalyticsConfigValid = (
  googleAnalytics:
    | CreatorsCampaignsWorkflowGoogleAnalyticsConfiguration
    | undefined,
): boolean => {
  return !isEmptyValue(googleAnalytics?.destinationUrl);
};

/**
 * @description Checks if the Impact configuration is valid.
 */
export const isImpactConfigValid = (
  impact: CreatorsCampaignsWorkflowImpactConfiguration | undefined,
): boolean => {
  return isTruthy(
    impact?.program?.id &&
      impact.program.name &&
      impact.deal?.id &&
      impact.deal.name &&
      impact.term?.id &&
      impact.term.name,
  );
};

export const clearImpact = (): CreatorsCampaignsWorkflowImpactConfiguration => {
  return {
    enabled: false,
  };
};

export const clearGoogleAnalyticsConfiguration =
  (): CreatorsCampaignsWorkflowGoogleAnalyticsConfiguration => {
    return {
      enabled: false,
    };
  };

export const clearStoreAndInfluencersCommissionConfig = (): {
  storeConfigurations: CreatorsCampaignsWorkflowStoreConfiguration;
  influencersCommission: InfluencersCommission;
} => {
  return {
    storeConfigurations: {
      pricingRule: {
        enabled: false,
      },
    },
    influencersCommission: {},
  };
};

const buildShopifyApiPayload = (
  affiliateFormData: AffiliateConfigurationFormValues,
) => {
  return {
    googleAnalyticsConfiguration: clearGoogleAnalyticsConfiguration(),
    impact: clearImpact(),
    storeConfigurations: {
      pricingRule: {
        enabled: true,
        pricingRuleId: affiliateFormData.shopify?.pricingRuleId,
      },
    },
    influencersCommission: {
      commissionType: affiliateFormData.influencersCommission?.commissionType,
      commissionValue:
        affiliateFormData.influencersCommission?.commissionType ===
        CreatorsCampaignInfluencerCommissionType.PERCENTAGE
          ? affiliateFormData.influencersCommission.commissionValuePercentage
          : affiliateFormData.influencersCommission?.commissionValueFixed,
      currency: affiliateFormData.influencersCommission?.currency,
      collectPaymentDetails: false,
    },
  };
};

const buildGoogleAnalyticsApiPayload = (
  affiliateFormData: AffiliateConfigurationFormValues,
) => {
  const clearedConfig = clearStoreAndInfluencersCommissionConfig();

  return {
    storeConfigurations: clearedConfig.storeConfigurations,
    influencersCommission: {},
    impact: clearImpact(),
    googleAnalyticsConfiguration: {
      enabled: true,
      destinationUrl: affiliateFormData.googleAnalytics?.destinationUrl,
    },
  };
};

const buildImpactApiPayload = (
  affiliateFormData: AffiliateConfigurationFormValues,
) => {
  const clearedConfig = clearStoreAndInfluencersCommissionConfig();

  return {
    storeConfigurations: clearedConfig.storeConfigurations,
    influencersCommission: {},
    googleAnalyticsConfiguration: clearGoogleAnalyticsConfiguration(),
    impact: {
      enabled: true,
      program:
        affiliateFormData.impact?.program?.id &&
        affiliateFormData.impact.program.name
          ? {
              id: affiliateFormData.impact.program.id,
              name: affiliateFormData.impact.program.name,
            }
          : undefined,
      deal:
        affiliateFormData.impact?.deal?.id && affiliateFormData.impact.deal.name
          ? {
              id: affiliateFormData.impact.deal.id,
              name: affiliateFormData.impact.deal.name,
            }
          : undefined,
      term:
        affiliateFormData.impact?.term?.id && affiliateFormData.impact.term.name
          ? {
              id: affiliateFormData.impact.term.id,
              name: affiliateFormData.impact.term.name,
            }
          : undefined,
    },
  };
};

/**
 * @description Transforms affiliate form data into the API payload format.
 */
export const transformAffiliateFormDataToApiPayload = (
  affiliateFormData: AffiliateConfigurationFormValues,
):
  | {
      googleAnalyticsConfiguration?: CreatorsCampaignsWorkflowGoogleAnalyticsConfiguration;
      impact?: CreatorsCampaignsWorkflowImpactConfiguration;
      influencersCommission?: InfluencersCommission;
      storeConfigurations?: CreatorsCampaignsWorkflowStoreConfiguration;
    }
  | undefined => {
  const { affiliatePartner } = affiliateFormData;

  if (
    affiliatePartner === CampaignWorkflowAffiliateConfigurationEnum.Shopify &&
    isStoreAndCommissionConfigValid({
      influencersCommission: affiliateFormData.influencersCommission,
      shopify: affiliateFormData.shopify,
    })
  ) {
    return buildShopifyApiPayload(affiliateFormData);
  }

  if (
    affiliatePartner ===
      CampaignWorkflowAffiliateConfigurationEnum.GoogleAnalytics &&
    isGoogleAnalyticsConfigValid(affiliateFormData.googleAnalytics)
  ) {
    return buildGoogleAnalyticsApiPayload(affiliateFormData);
  }

  if (
    affiliatePartner === CampaignWorkflowAffiliateConfigurationEnum.Impact &&
    isImpactConfigValid(affiliateFormData.impact)
  ) {
    return buildImpactApiPayload(affiliateFormData);
  }

  return undefined;
};

export const getAffiliatePartnerFormValueAsEnum = (
  stringValue: string | undefined,
): CampaignWorkflowAffiliateConfigurationEnum | undefined => {
  switch (stringValue) {
    case CampaignWorkflowAffiliateConfigurationEnum.Shopify:
      return CampaignWorkflowAffiliateConfigurationEnum.Shopify;
    case CampaignWorkflowAffiliateConfigurationEnum.GoogleAnalytics:
      return CampaignWorkflowAffiliateConfigurationEnum.GoogleAnalytics;
    case CampaignWorkflowAffiliateConfigurationEnum.Impact:
      return CampaignWorkflowAffiliateConfigurationEnum.Impact;
    default:
      return;
  }
};
